/**
 * Per-carrier writing-number validation rules.
 *
 * These are NOT guesses — each rule was verified by probing Techficient's
 * staging /user/create API directly (submitting deliberately short/long/
 * alphanumeric numbers and reading back the exact ValidationError):
 *
 *   american-amicable      → "Agent writing number must be exactly 10 digits."
 *   uhl                    → "Contract numbers must be exactly 10 characters."
 *   fidelity-and-guaranty  → "The agent writing number must be 9-10 letters and/or numbers."
 *   sbli                   → per-PRODUCT number (ProductCode required) — structural, not length
 *   everyone else          → no length rule (accepted a 5-digit number)
 *
 * Importantly, the API ACCEPTS LETTERS for F&G and UHL — so a digits-only
 * rule would wrongly reject valid writing numbers there. Only American
 * Amicable is digits-only.
 */
export interface WritingNumberRule {
  /** Exact required length (mutually exclusive with min/max). */
  exact?: number;
  min?: number;
  max?: number;
  /** true = letters allowed (alphanumeric); false = digits only. */
  alpha: boolean;
  /** Short human hint shown under the input / as the error. */
  hint: string;
  /**
   * Left-pad shorter digit strings with zeros up to this width before
   * submitting. American Amicable requires a 10-digit number but issues
   * agents SHORTER numbers (commonly 7 digits) — Techficient confirmed
   * (call 2026-07-20) that the correct format is the number zero-padded to
   * 10 (e.g. 1234567 → 0001234567). Applied on normalize, not while typing.
   */
  padTo?: number;
}

export const WRITING_NUMBER_RULES: Record<string, WritingNumberRule> = {
  "american-amicable": {
    exact: 10,
    alpha: false,
    padTo: 10,
    // Accept the agent's real (shorter) number too — we pad it.
    min: 5,
    max: 10,
    hint: "7–10 digits (padded to 10)",
  },
  uhl: {
    exact: 10,
    alpha: true,
    hint: "Exactly 10 characters",
  },
  "fidelity-and-guaranty": {
    min: 9,
    max: 10,
    alpha: true,
    hint: "9–10 letters or numbers",
  },
};

/** Loose default for carriers with no documented rule — just catches garbage. */
const DEFAULT_RULE: WritingNumberRule = {
  min: 3,
  max: 20,
  alpha: true,
  hint: "",
};

export function ruleForCarrier(carrierCode: string): WritingNumberRule {
  return WRITING_NUMBER_RULES[carrierCode] ?? DEFAULT_RULE;
}

/**
 * Validate an entered writing number against its carrier's rule.
 * Returns null when valid (or empty — carriers are optional), or a short
 * error string when it violates the rule.
 */
export function validateWritingNumber(
  carrierCode: string,
  value: string,
): string | null {
  const v = (value || "").trim();
  if (!v) return null; // empty is allowed (optional)
  const rule = ruleForCarrier(carrierCode);

  if (!rule.alpha && !/^[0-9]+$/.test(v)) return "Digits only";
  if (rule.alpha && !/^[a-zA-Z0-9]+$/.test(v)) return "Letters and numbers only";

  // When the carrier pads shorter numbers (AmAm), a min/max window governs
  // instead of an exact length — anything in-window normalizes to `padTo`.
  if (rule.exact != null && rule.padTo == null && v.length !== rule.exact) {
    return rule.hint || `Must be exactly ${rule.exact} characters`;
  }
  if (rule.min != null && v.length < rule.min) {
    return rule.hint || `Too short`;
  }
  if (rule.max != null && v.length > rule.max) {
    return rule.hint || `Too long`;
  }
  return null;
}

/**
 * Convert an agent-entered writing number to the exact form the carrier's
 * API expects. Today this only pads American Amicable's short numbers to
 * 10 digits; everything else passes through trimmed. Empty stays empty.
 */
export function normalizeWritingNumber(
  carrierCode: string,
  value: string,
): string {
  const v = (value || "").trim();
  if (!v) return v;
  const rule = ruleForCarrier(carrierCode);
  if (rule.padTo && !rule.alpha && /^[0-9]+$/.test(v) && v.length < rule.padTo) {
    return v.padStart(rule.padTo, "0");
  }
  return v;
}

/**
 * What characters to allow in the input as the agent types. Digits-only
 * carriers strip letters; the rest allow alphanumeric.
 */
export function sanitizeWritingNumberInput(
  carrierCode: string,
  raw: string,
): string {
  const rule = ruleForCarrier(carrierCode);
  const cleaned = rule.alpha
    ? raw.replace(/[^a-zA-Z0-9]/g, "")
    : raw.replace(/\D/g, "");
  const max = rule.exact ?? rule.max ?? 20;
  return cleaned.slice(0, max);
}
